import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, MessageCircle, Mail, FileText } from 'lucide-react';

const HelpPage = () => {
  const faqs = [
    {
      question: "How do I reset my password?",
      answer: "Currently, password reset is handled through the profile page when logged in. For account recovery, please contact support."
    },
    {
      question: "Can I share tasks with other users?",
      answer: "Task sharing is planned for a future update. Currently, tasks are private to each user account."
    },
    {
      question: "How long are tasks stored?",
      answer: "Tasks are stored indefinitely unless you manually delete them. Your data is securely backed up on Cloudflare's global network."
    },
    {
      question: "Is there a mobile app?",
      answer: "NebiusFlow is a progressive web app that works great on mobile browsers. Native apps are planned for the future."
    },
    {
      question: "Can I export my tasks?",
      answer: "Export functionality is coming soon. You'll be able to export tasks in CSV and JSON formats."
    },
    {
      question: "How secure is my data?",
      answer: "Very secure! We use JWT authentication, encrypted passwords, HTTPS everywhere, and Cloudflare's enterprise-grade infrastructure."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-6">Help Center</h1>
          <p className="text-xl text-gray-600 mb-12">
            Find answers to common questions and get the support you need
          </p>

          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <h3 className="text-lg font-semibold mb-2 flex items-start">
                    <HelpCircle className="w-5 h-5 text-primary-600 mr-2 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 ml-7">{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/documentation" className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <FileText className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Documentation</h3>
              <p className="text-gray-600">Read our comprehensive guides</p>
            </Link>

            <Link to="/contact" className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <Mail className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Email Support</h3>
              <p className="text-gray-600">Get help from our team</p>
            </Link>

            <Link to="/blog" className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <MessageCircle className="w-10 h-10 text-primary-600 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Community</h3>
              <p className="text-gray-600">Learn from other users</p>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HelpPage;
